import { Suspense, useEffect } from 'react';
import { AppShell, Burger, Group, Title, Text, Divider, Stack, Button, Loader, Center, ActionIcon, Tooltip, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Outlet, useLocation } from 'react-router-dom';
import { IconLogout } from '@tabler/icons-react';
import { NavItems, findNavItem } from './NavItems';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../auth/AuthContext';
import { ZONE_COLOURS } from '../theme';
import { IssueReporterFab } from '../components/IssueReporter/IssueReporterFab';
import { useEditMode } from '../context/EditModeContext';
import { IconPencil, IconPencilOff } from '@tabler/icons-react';

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { logout } = useAuth();
  const { pathname } = useLocation();
  const { isEditMode, toggleEditMode } = useEditMode();

  // Picking a page closes the mobile nav drawer — native drawers dismiss
  // on selection; leaving it open buries the page it just navigated to.
  useEffect(() => { close(); }, [pathname, close]);

  // Title and zone both derive from the nav registry — one source of truth.
  const navItem = findNavItem(pathname);
  const zoneColour = ZONE_COLOURS[navItem?.zone ?? 'teal'];
  const pageTitle = navItem?.label ?? 'Claudia';

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
      styles={{
        header: {
          borderTop: `3px solid var(--mantine-color-${zoneColour}-4)`,
        },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={3}>{pageTitle}</Title>
          </Group>
          <Group>
            <Tooltip label={isEditMode ? 'Exit edit mode' : 'Edit page content'}>
              <ActionIcon
                aria-label={isEditMode ? 'Exit edit mode' : 'Edit page content'}
                variant={isEditMode ? 'filled' : 'subtle'}
                color={isEditMode ? 'blue' : 'gray'}
                onClick={toggleEditMode}
              >
                {isEditMode ? <IconPencilOff size={18} /> : <IconPencil size={18} />}
              </ActionIcon>
            </Tooltip>
            <ThemeToggle />
            <Text size="sm" c="dimmed" style={{ cursor: 'default' }} visibleFrom="sm">Signed in · Adele</Text>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section>
          <Group p="xs" mb="xs">
            <Text fw={700} size="lg" c={zoneColour}>Claudia</Text>
          </Group>
        </AppShell.Section>

        {/* The nav list is taller than a phone screen — the middle section
            scrolls while the Claudia header and Logout stay pinned. */}
        <AppShell.Section grow component={ScrollArea}>
          <NavItems />
        </AppShell.Section>

        <AppShell.Section>
          <Divider my="xs" />
          <Stack gap="xs" p="xs">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconLogout size={16} />}
              onClick={logout}
              justify="start"
              fullWidth
            >
              Logout
            </Button>
          </Stack>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Suspense fallback={<Center h="50vh"><Loader size="lg" /></Center>}>
          <Outlet />
        </Suspense>
        <IssueReporterFab />
      </AppShell.Main>
    </AppShell>
  );
}
